import React, { useEffect, useMemo, useState } from "react";
import {
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllWorkflowsQuery,
  useGetAllTeamsSelectedWorkflowIdQuery,
  useGetTicketSlVolumeReportQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import {
  useGetTicketTagSLAEscalationLevelsQuery,
  useStatusQuery,
} from "../../../../Services/settingsAPIs/CommonAPI";
import { toast } from "react-toastify";

const REPORT_TITLE = "Ticket SL Volume";

type SlReportType = "tag" | "priority" | "workflow";

const REPORT_TYPE_OPTIONS: { label: string; value: SlReportType }[] = [
  { label: "Customer Tag SL", value: "tag" },
  { label: "Priority SL", value: "priority" },
  { label: "Workflow SL", value: "workflow" },
];

const PRIORITY_FILTER_OPTIONS = ["Urgent", "High", "Medium", "Low"];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 200,
    },
  },
};

const extractRows = (reportData: unknown): any[] => {
  const payload = reportData as Record<string, unknown>;
  if (Array.isArray(payload?.results)) return payload.results as any[];
  if (Array.isArray(payload?.data)) return payload.data as any[];
  if (Array.isArray(reportData)) return reportData as any[];
  return [];
};

const buildColumnsFromRow = (row: Record<string, unknown>): GridColDef[] => {
  const keys = Object.keys(row).filter((key) => key !== "id" && key !== "sno");
  return [
    {
      field: "sno",
      headerName: "S.No.",
      width: 80,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      sortable: false,
    },
    ...keys.map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      minWidth: 120,
      align: "center" as const,
      headerAlign: "center" as const,
    })),
  ];
};

const processRowValues = (row: Record<string, unknown>) => {
  const processed: Record<string, unknown> = {};
  Object.entries(row).forEach(([key, value]) => {
    processed[key] =
      value === null || value === undefined || value === "" ? "-" : value;
  });
  return processed;
};

const TicketSlVolumeReport = () => {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState<SlReportType>("tag");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [params, setParams] = useState<any>(null);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedWorkflowStatuses, setSelectedWorkflowStatuses] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 20, page: 0 });

  const { data: ticketTags, isLoading: tagsLoading } =
    useGetTicketTagSLAEscalationLevelsQuery({});
  const { data: workflowsData, isLoading: workflowsLoading } =
    useGetAllWorkflowsQuery({});
  const { data: teamData, isLoading: teamLoading } =
    useGetAllTeamsSelectedWorkflowIdQuery(selectedWorkflowId, {
      skip: !selectedWorkflowId,
    });
  const { data: workflowStatusData, isLoading: workflowStagesLoading } =
    useStatusQuery(selectedWorkflowId, {
      skip: !selectedWorkflowId || reportType !== "workflow",
    });
  const {
    data: reportData,
    isLoading: reportLoading,
    isFetching: reportFetching,
    isError,
    error,
    refetch,
  } = useGetTicketSlVolumeReportQuery(params, { skip: !params });

  const theTeams = useMemo(
    () =>
      Array.isArray(teamData?.results)
        ? teamData.results.map((team: any) => ({
            id: team.id,
            name: team.team_name,
          }))
        : [],
    [teamData],
  );
  const theWorkflows = useMemo(
    () => (Array.isArray(workflowsData?.results) ? workflowsData.results : []),
    [workflowsData],
  );
  const tagOptions = useMemo(
    () =>
      Array.isArray(ticketTags)
        ? ticketTags
            .map((tag: any) => ({
              id: String(tag.tag_id ?? tag.id ?? ""),
              name: tag.tag ?? tag.tag_name ?? tag.name ?? "Unknown tag",
            }))
            .filter((tag) => tag.id)
        : [],
    [ticketTags],
  );
  const allTagIds = useMemo(() => tagOptions.map((tag) => tag.id), [tagOptions]);
  const workflowStatusOptions = useMemo(() => {
    if (!Array.isArray(workflowStatusData?.all_statuses)) return [];
    return workflowStatusData.all_statuses
      .map((stage: any) => {
        const statusType = String(stage.status_type ?? "").toLowerCase();
        if (statusType === "active" || statusType === "reopen") {
          return typeof stage.status === "string" ? stage.status.trim() : "";
        }
        return String(stage.status?.status ?? "").trim();
      })
      .filter((name: string) => name.length > 0);
  }, [workflowStatusData]);

  const isAllTagsSelected =
    allTagIds.length > 0 && selectedTagIds.length === allTagIds.length;
  const isAllPrioritiesSelected =
    selectedPriorities.length === PRIORITY_FILTER_OPTIONS.length;
  const isAllWorkflowStatusesSelected =
    selectedWorkflowStatuses.length === workflowStatusOptions.length &&
    workflowStatusOptions.length > 0;

  const getTagIdsForPayload = () => {
    if (!allTagIds.length) return "";
    if (!selectedTagIds.length || isAllTagsSelected) return allTagIds.join(",");
    return selectedTagIds.join(",");
  };

  const getWorkflowStatusesForPayload = () => {
    if (!workflowStatusOptions.length) return "";
    if (!selectedWorkflowStatuses.length || isAllWorkflowStatusesSelected) {
      return workflowStatusOptions.join(",");
    }
    return selectedWorkflowStatuses.join(",");
  };

  const serializeParams = (queryParams: Record<string, string | number>) =>
    JSON.stringify(queryParams, Object.keys(queryParams).sort());

  useEffect(() => {
    if (!params) {
      setDataRows([]);
      setColumns([]);
      return;
    }
    if (isError) {
      toast.error(
        (error as { data?: { message?: string } })?.data?.message ||
          "Failed to fetch Ticket SL Volume report",
      );
      setDataRows([]);
      setColumns([]);
      return;
    }
    if (!reportData) return;

    const rows = extractRows(reportData);

    if (rows.length > 0) {
      const processedRows = rows.map((row, index) => ({
        id: index + 1,
        ...processRowValues(row as Record<string, unknown>),
      }));
      setColumns(buildColumnsFromRow(processedRows[0]));
      setDataRows(processedRows);
    } else {
      setColumns([]);
      setDataRows([]);
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [params, reportData, isError, error]);

  const handleReportTypeChange = (type: SlReportType) => {
    setReportType(type);
    setParams(null);
    setDataRows([]);
    setColumns([]);
    setSelectedTagIds([]);
    setSelectedPriorities([]);
    setSelectedWorkflowStatuses([]);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const handleWorkflowChange = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    setSelectedTeamId("");
    setSelectedWorkflowStatuses([]);
  };

  const handleSubmit = () => {
    if (!dateFrom) return toast.error('Please select "From Date"');
    if (!dateTo) return toast.error('Please select "To Date"');
    if (!selectedTeamId) return toast.error('Please select "Team"');
    if (!selectedWorkflowId) return toast.error('Please select "Workflow"');

    const queryParams: Record<string, string | number> = {
      gte: dateFrom,
      lte: dateTo,
      sl_type: reportType,
      team_id: selectedTeamId,
      workflow_id: selectedWorkflowId,
    };

    if (reportType === "tag") {
      const tagIds = getTagIdsForPayload();
      if (!tagIds) return toast.error("No customer tags available to filter");
      queryParams.tag_id = tagIds;
    }
    if (
      (reportType === "priority" || reportType === "workflow") &&
      selectedPriorities.length &&
      !isAllPrioritiesSelected
    ) {
      queryParams.priority = selectedPriorities.join(",");
    }
    if (reportType === "workflow") {
      const workflowStatuses = getWorkflowStatusesForPayload();
      if (workflowStatuses) {
        queryParams.workflow_status = workflowStatuses;
      }
    }

    if (params && serializeParams(params) === serializeParams(queryParams)) {
      setParams(queryParams);
      refetch();
      return;
    }

    setParams(queryParams);
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setSelectedTeamId("");
    setSelectedWorkflowId("");
    setSelectedTagIds([]);
    setSelectedPriorities([]);
    setSelectedWorkflowStatuses([]);
    setDataRows([]);
    setColumns([]);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const handleDownload = () => {
    if (!params || dataRows.length === 0 || columns.length === 0) {
      toast.error("Please apply filters and submit before downloading.");
      return;
    }
    const headers = columns.map((col) => col.headerName ?? col.field);
    const fields = columns.map((col) => String(col.field));
    const csvContent = [
      headers.map((h) => `"${h}"`).join(","),
      ...dataRows.map((row, index) =>
        fields
          .map((field) => {
            const value =
              field === "sno" ? index + 1 : row[field];
            return `"${String(value ?? "").replace(/"/g, '""')}"`;
          })
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Ticket_SL_Volume_${reportType}_Report.csv`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    link.remove();
    toast.success("Download started.");
  };

  const toggleMultiSelect = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    allOptions: string[],
  ) => {
    if (value === "all") {
      setSelected((prev) =>
        prev.length === allOptions.length ? [] : [...allOptions],
      );
      return;
    }
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const paginatedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return dataRows
      .slice(start, start + paginationModel.pageSize)
      .map((row, index) => ({
        ...row,
        sno: start + index + 1,
      }));
  }, [dataRows, paginationModel.page, paginationModel.pageSize]);

  const isLoading =
    workflowsLoading ||
    teamLoading ||
    tagsLoading ||
    (reportType === "workflow" && workflowStagesLoading) ||
    reportLoading ||
    reportFetching;

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          <button
            className="btn btn-link mb-2"
            onClick={() => navigate("/reports/ticket/reports-list")}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{ fontSize: "20px", color: "black", cursor: "pointer" }}
            />
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">{REPORT_TITLE}</span>
        </div>

        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10 flex-wrap"
          style={{ marginLeft: "30px" }}
        >
            <div className="w-100 mw-200px">
              <FormControl fullWidth>
                <InputLabel id="report-type-label">Report type</InputLabel>
                <Select
                  labelId="report-type-label"
                  label="Report type"
                  value={reportType}
                  onChange={(e) =>
                    handleReportTypeChange(e.target.value as SlReportType)
                  }
                  MenuProps={MenuProps}
                >
                  {REPORT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="w-100 mw-150px">
              <TextField
                id="date-from"
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputProps: {
                    lang: "en-GB",
                    style: { textTransform: "uppercase" },
                    max: new Date().toISOString().split("T")[0],
                  },
                }}
                fullWidth
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="w-100 mw-150px">
              <TextField
                id="date-to"
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputProps: {
                    lang: "en-GB",
                    style: { textTransform: "uppercase" },
                    max: new Date().toISOString().split("T")[0],
                  },
                }}
                fullWidth
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="w-100 mw-200px">
              <FormControl fullWidth>
                <InputLabel id="workflow-select-label">Workflow</InputLabel>
                <Select
                  labelId="workflow-select-label"
                  value={selectedWorkflowId}
                  label="Workflow"
                  onChange={(e) => handleWorkflowChange(String(e.target.value))}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>Select workflow</em>
                  </MenuItem>
                  {theWorkflows.map((workflow: any) => (
                    <MenuItem key={workflow.id} value={workflow.id}>
                      {workflow.workflow_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="w-100 mw-200px">
              <FormControl fullWidth>
                <InputLabel id="team-select-label">Select teams</InputLabel>
                <Select
                  labelId="team-select-label"
                  value={selectedTeamId}
                  label="Select teams"
                  disabled={!selectedWorkflowId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>Select team</em>
                  </MenuItem>
                  {theTeams.map((team: any) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {reportType === "tag" && (
              <div className="w-100 mw-200px">
                <FormControl fullWidth>
                  <InputLabel id="tag-select-label">Customer tag</InputLabel>
                  <Select
                    labelId="tag-select-label"
                    multiple
                    value={selectedTagIds}
                    input={<OutlinedInput label="Customer tag" />}
                    renderValue={(selected) => {
                      if (isAllTagsSelected || selected.length === 0) {
                        return <div>All</div>;
                      }
                      const names = tagOptions
                        .filter((tag) => selected.includes(tag.id))
                        .map((tag) => tag.name);
                      return <div>{names.join(", ")}</div>;
                    }}
                    MenuProps={MenuProps}
                  >
                    <MenuItem
                      value="all"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleMultiSelect("all", setSelectedTagIds, allTagIds);
                      }}
                    >
                      <Checkbox
                        checked={isAllTagsSelected}
                        indeterminate={
                          selectedTagIds.length > 0 && !isAllTagsSelected
                        }
                      />
                      <ListItemText primary="All" />
                    </MenuItem>
                    {tagOptions.map((tag) => (
                      <MenuItem
                        key={tag.id}
                        value={tag.id}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleMultiSelect(tag.id, setSelectedTagIds, allTagIds);
                        }}
                      >
                        <Checkbox checked={selectedTagIds.includes(tag.id)} />
                        <ListItemText primary={tag.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}

            {reportType === "priority" && (
              <div className="w-100 mw-200px">
                <FormControl fullWidth>
                  <InputLabel id="priority-select-label">Priority</InputLabel>
                  <Select
                    labelId="priority-select-label"
                    multiple
                    value={selectedPriorities}
                    input={<OutlinedInput label="Priority" />}
                    renderValue={(selected) =>
                      isAllPrioritiesSelected || selected.length === 0 ? (
                        <div>All</div>
                      ) : (
                        <div>{selected.join(", ")}</div>
                      )
                    }
                    MenuProps={MenuProps}
                  >
                    <MenuItem
                      value="all"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleMultiSelect(
                          "all",
                          setSelectedPriorities,
                          PRIORITY_FILTER_OPTIONS,
                        );
                      }}
                    >
                      <Checkbox
                        checked={isAllPrioritiesSelected}
                        indeterminate={
                          selectedPriorities.length > 0 &&
                          !isAllPrioritiesSelected
                        }
                      />
                      <ListItemText primary="All" />
                    </MenuItem>
                    {PRIORITY_FILTER_OPTIONS.map((priority) => (
                      <MenuItem
                        key={priority}
                        value={priority}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleMultiSelect(
                            priority,
                            setSelectedPriorities,
                            PRIORITY_FILTER_OPTIONS,
                          );
                        }}
                      >
                        <Checkbox
                          checked={selectedPriorities.includes(priority)}
                        />
                        <ListItemText primary={priority} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}

            {reportType === "workflow" && (
              <>
                <div className="w-100 mw-200px">
                  <FormControl fullWidth>
                    <InputLabel id="priority-select-label">Priority</InputLabel>
                    <Select
                      labelId="priority-select-label"
                      multiple
                      value={selectedPriorities}
                      input={<OutlinedInput label="Priority" />}
                      renderValue={(selected) =>
                        isAllPrioritiesSelected || selected.length === 0 ? (
                          <div>All</div>
                        ) : (
                          <div>{selected.join(", ")}</div>
                        )
                      }
                      MenuProps={MenuProps}
                    >
                      <MenuItem
                        value="all"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleMultiSelect(
                            "all",
                            setSelectedPriorities,
                            PRIORITY_FILTER_OPTIONS,
                          );
                        }}
                      >
                        <Checkbox
                          checked={isAllPrioritiesSelected}
                          indeterminate={
                            selectedPriorities.length > 0 &&
                            !isAllPrioritiesSelected
                          }
                        />
                        <ListItemText primary="All" />
                      </MenuItem>
                      {PRIORITY_FILTER_OPTIONS.map((priority) => (
                        <MenuItem
                          key={priority}
                          value={priority}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleMultiSelect(
                              priority,
                              setSelectedPriorities,
                              PRIORITY_FILTER_OPTIONS,
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedPriorities.includes(priority)}
                          />
                          <ListItemText primary={priority} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="w-100 mw-200px">
                  <FormControl fullWidth>
                    <InputLabel id="workflow-status-label">
                      Workflow Status
                    </InputLabel>
                    <Select
                      labelId="workflow-status-label"
                      multiple
                      value={selectedWorkflowStatuses}
                      disabled={!selectedWorkflowId || workflowStagesLoading}
                      input={<OutlinedInput label="Workflow Status" />}
                      renderValue={(selected) =>
                        isAllWorkflowStatusesSelected ||
                        selected.length === 0 ? (
                          <div>All</div>
                        ) : (
                          <div>{selected.join(", ")}</div>
                        )
                      }
                      MenuProps={MenuProps}
                    >
                      <MenuItem
                        value="all"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleMultiSelect(
                            "all",
                            setSelectedWorkflowStatuses,
                            workflowStatusOptions,
                          );
                        }}
                      >
                        <Checkbox
                          checked={isAllWorkflowStatusesSelected}
                          indeterminate={
                            selectedWorkflowStatuses.length > 0 &&
                            !isAllWorkflowStatusesSelected
                          }
                        />
                        <ListItemText primary="All" />
                      </MenuItem>
                      {workflowStatusOptions.map((status) => (
                        <MenuItem
                          key={status}
                          value={status}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleMultiSelect(
                              status,
                              setSelectedWorkflowStatuses,
                              workflowStatusOptions,
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedWorkflowStatuses.includes(status)}
                          />
                          <ListItemText primary={status} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </>
            )}

            <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
              <button className="btn btn-primary" onClick={handleSubmit}>
                Submit
              </button>
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || dataRows.length === 0}
              >
                Download
              </button>
              <button className="btn btn-link" onClick={resetFilters}>
                <i className="bi bi-funnel fs-2 text-dark" />
              </button>
            </div>
          </div>

        <CardContent style={{ width: "100%" }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={paginatedRows}
              columns={columns}
              pagination
              paginationMode="server"
              rowCount={dataRows.length}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 15, 20]}
              disableColumnMenu
              localeText={{
                noRowsLabel: "No rows found",
              }}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "lightgray",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": { justifyContent: "flex-end" },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  overflow: "visible",
                  whiteSpace: "normal",
                  textOverflow: "initial",
                  lineHeight: "normal",
                  textAlign: "center",
                },
                "& .MuiDataGrid-columnsContainer": {
                  backgroundColor: "lightgray",
                },
                "& .MuiDataGrid-cell": {
                  textAlign: "center",
                },
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default TicketSlVolumeReport;
